import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { LeftNavigationBar } from 'screens/Main/components/LeftNavigationBar/LeftNavigationBar';
import { TopNavigationBar } from 'screens/Main/components/TopNavigationBar/TopNavigationBar';
import Footer from 'components/Footer/Footer';
import LoginPromptModal from 'components/LoginPromptModal/LoginPromptModal';
import { MainRoutesProps } from 'routes';
import CardPrintingsHeader from 'components/CardPrintingsHeader/CardPrintingsHeader';
import CardPrintingsContainer from 'components/CardPrintingsContainer/CardPrintingsContainer';
import QtyControl from 'components/QtyControl/QtyControl';
import ActionButton from 'components/ActionButton/ActionButton';
import GenericGroupContainer from 'components/GenericGroupContainer/GenericGroupContainer';
import { Points } from 'components/Points/Points';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { FoilCardImage } from 'components/FoilCardImage';
import { FinishMarks } from 'components/FinishMarks';
import { CardFinish, FINISH_ORDER, FinishSource, getAvailableFinishes } from 'utils/finishes';
import CardImageTooltip from 'components/CardLink/CardImageTooltip';
import { useSearchCardsQuery, useGetCardPrintingsQuery, useGetCardByIdQuery } from 'store/services/cards/search/cardsSearchApi';
import {
  useAddFromPrintingsMutation,
  useUpdateWantListPreferencesMutation,
  useGetWantListEntryQuery,
  useCardToWantListQuantityUpdateMutation,
  type AddFromPrintingsRequest
} from 'store/services/collectionList/wantList/wantListApi';
import { useGetWantListDefaultsQuery } from 'store/services/settings/settingsApi';
import { decimalToPoints } from 'utils/decimalUtils';
import { checkEndpointHealth, getCurrentUserId } from 'utils/backendHealthCheck';
import { AssetUrls } from 'config/assets';
import {
  showError,
  showCardAddedSuccess,
  showAuthError,
  showValidationError
} from 'utils/toastNotifications';
import { mapFrontendToBackend } from 'constants/languages';
import './ViewCardPrintings.css';
import { showFinishAdjustment } from 'utils/toastNotifications';

interface Props {
  currentTab: MainRoutesProps;
}

interface CardPrinting {
  id: number;
  name: string;
  artist: string;
  imageUrl: string;
  price: any;
  scryfallId?: string;
  imageUris?: any;
  setName?: string;
  collectorNumber?: string;
  foil?: boolean;
  nonFoil?: boolean;
  /** Scryfall finishes array, verbatim; read through getAvailableFinishes */
  finishes?: string[] | null;
  /** Scryfall promo_types, verbatim; rendered as read-only chips (D-6) */
  promoTypes?: string[] | null;
  borderColor?: string;
}

interface GroupedCards {
  groupKey: string;
  groupLabel: string;
  cards: CardPrinting[];
  minPrice: number | null;
  maxPrice: number | null;
}

export default function ViewCardPrintings({ currentTab }: Props) {
  const { cardName } = useParams<{ cardName: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Search and the Home Recent Activity tiles (which reuse Search's tile and
  // menu) both hand over a return path; the source keeps them apart for telemetry.
  const navFrom: string | undefined = location.state?.from;
  const fromSearch = navFrom === 'search' || navFrom === 'home';
  const returnPath = location.state?.returnPath;
  const editWantId = searchParams.get('editWantId');
  const cardIdParam = searchParams.get('cardId');
  const isEditMode = !!editWantId;

  // Auth state for login modal
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // View mode state (persisted to localStorage)
  const [viewMode, setViewMode] = useState<'image' | 'list'>(() => {
    const saved = localStorage.getItem('printingsViewMode');
    return (saved === 'list') ? 'list' : 'image';
  });
  const handleViewModeChange = (mode: 'image' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('printingsViewMode', mode);
  };

  // Tooltip state for list view hover
  const [hoveredCard, setHoveredCard] = useState<{ scryfallId: string; cardName: string } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const handleCardHover = useCallback((e: React.MouseEvent, scryfallId: string, cardName: string) => {
    if (isMobile) return;
    setTooltipPosition({ x: e.clientX, y: e.clientY });
    setHoveredCard({ scryfallId, cardName });
  }, [isMobile]);
  const handleCardHoverLeave = useCallback(() => {
    setHoveredCard(null);
  }, []);

  // Initialize with empty set for edit mode, wait for data to load
  const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set());
  const [groupBy, setGroupBy] = useState<string>('Artist');
  const [removedGroups, setRemovedGroups] = useState<Set<string>>(new Set());
  const [priceFilter, setPriceFilter] = useState<{ min: number | null; max: number | null }>({ min: null, max: null });
  const [setFilter, setSetFilter] = useState<string | null>(null); // null = All Sets
  const [quantity, setQuantity] = useState<number>(1);
  const [hasInitializedCards, setHasInitializedCards] = useState(false);
  const [hasInitializedPreferences, setHasInitializedPreferences] = useState(false);

  // Get want list defaults (only used when NOT in edit mode)
  const { data: wantListDefaults } = useGetWantListDefaultsQuery();

  // Initialize preferences based on defaults (if not in edit mode)
  const getInitialFoilPreferences = (): string[] => {
    if (isEditMode) return []; // Will be loaded from existing entry
    const prefs: string[] = [];
    if (wantListDefaults?.defaultAcceptNonFoil) prefs.push('nonfoil');
    if (wantListDefaults?.defaultAcceptFoil) prefs.push('foil');
    if (wantListDefaults?.defaultAcceptEtched ?? wantListDefaults?.defaultAcceptFoil) prefs.push('etched');
    return prefs.length > 0 ? prefs : ['nonfoil', 'foil', 'etched']; // Fallback
  };

  const getInitialLanguagePreferences = (): string[] => {
    if (isEditMode) return []; // Will be loaded from existing entry
    // If navigated with ?lang=xx, pre-select that language
    const langParam = searchParams.get('lang');
    if (langParam) {
      return [langParam.toUpperCase()];
    }
    return wantListDefaults?.defaultLanguages || ['EN']; // Fallback to English
  };

  const getInitialConditionPreferences = (): string[] => {
    if (isEditMode) return []; // Will be loaded from existing entry
    return wantListDefaults?.defaultConditions || ['NM', 'LP', 'MP']; // Fallback
  };

  const [foilPreferences, setFoilPreferences] = useState<string[]>(getInitialFoilPreferences);
  const [languagePreferences, setLanguagePreferences] = useState<string[]>(getInitialLanguagePreferences);
  const [conditionPreferences, setConditionPreferences] = useState<string[]>(getInitialConditionPreferences);
  const [addFromPrintings, { isLoading: isAddingToWantList }] = useAddFromPrintingsMutation();
  const [updateWantListPreferences, { isLoading: isUpdatingWantList }] = useUpdateWantListPreferencesMutation();
  const [updateQuantity] = useCardToWantListQuantityUpdateMutation();

  // Fetch existing want list entry if in edit mode
  const { data: existingWantEntry, isLoading: isLoadingWantEntry } = useGetWantListEntryQuery(
    editWantId || '',
    { skip: !editWantId }
  );

  // Update preferences when defaults change (only when NOT in edit mode)
  useEffect(() => {
    if (!isEditMode && wantListDefaults && !hasInitializedPreferences) {
      setFoilPreferences(getInitialFoilPreferences());
      setLanguagePreferences(getInitialLanguagePreferences());
      setConditionPreferences(getInitialConditionPreferences());
      setHasInitializedPreferences(true); // Mark preferences as initialized
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantListDefaults, isEditMode, hasInitializedPreferences]);
  
  // Mobile detection effect
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Decode the card name from URL
  const decodedCardName = cardName ? decodeURIComponent(cardName) : '';

  // If we have a cardId from navigation, use it directly; otherwise search by name
  const shouldSearch = !cardIdParam && decodedCardName;

  // Search for card by name only if we don't have a cardId
  const { data: searchResults, isLoading: isSearching, error: searchError } = useSearchCardsQuery(
    { name: decodedCardName, exact: true, limit: 1 },
    { skip: !shouldSearch }
  );

  // Fetch card details when we have a cardId to get the oracle ID
  const { data: cardDetails } = useGetCardByIdQuery(
    cardIdParam || '',
    { skip: !cardIdParam }
  );

  // Get the card ID either from the URL parameter or from search results
  const firstSearchResult = searchResults?.cards?.[0];
  const firstCardId = cardIdParam || firstSearchResult?.id;
  const searchOracleId = firstSearchResult?.oracleId;

  // Then fetch all printings using that ID (which includes price data)
  const { data: printingsData, isLoading: isLoadingPrintings, error: printingsError } = useGetCardPrintingsQuery(
    { cardId: firstCardId || '' },
    { skip: !firstCardId }
  );
  
  // Language multiplier from card detail response (applied server-side to pointValue)
  const languageMultiplier = (cardDetails as any)?.multiplierValue ?? 1;

  // Helper function to get card price with language multiplier applied
  const getCardPrice = (card: any): number | null => {
    // Prefer pointValue if available (already has language multiplier from backend)
    if (card.pointValue && card.pointValue > 0) {
      return card.pointValue;
    }

    let price: number | null = null;
    const priceData = Array.isArray(card.price) && card.price.length > 0 ? card.price[0] : card.price;

    if (priceData) {
      if (priceData.usd !== null && priceData.usd !== undefined) {
        price = decimalToPoints(priceData.usd);
      } else if (priceData.usdFoil !== null && priceData.usdFoil !== undefined) {
        price = decimalToPoints(priceData.usdFoil);
      } else if (priceData.usdEtched !== null && priceData.usdEtched !== undefined) {
        // Etched-only printings carry no usd / usdFoil; their own column is the real value
        price = decimalToPoints(priceData.usdEtched);
      }
    }

    if (price && languageMultiplier !== 1) {
      price = Math.round(price * languageMultiplier);
    }

    return price;
  };

  // Combine loading states (only check search loading if we're actually searching)
  const isLoading = (shouldSearch && isSearching) || isLoadingPrintings;
  const error = (shouldSearch && searchError) || printingsError;
  
  // Initialize selected cards and preferences when data loads
  useEffect(() => {
    // Don't initialize anything until we have all the data we need
    if (!printingsData) {
      return;
    }
    
    if (isEditMode) {
      // In edit mode, wait for the want entry to load
      if (isLoadingWantEntry) {
        return;
      }
      
      if (!existingWantEntry) {
        return;
      }

      if (!hasInitializedPreferences) {

        // In edit mode, select only cards matching the acceptable sets or specific IDs
        const matchingCardIds = new Set<number>();
        
        // First check if we have specific printing IDs (most precise)
        const acceptedPrintingIds = (existingWantEntry as any).acceptedPrintingIds || [];
        const acceptableSetCodes = (existingWantEntry as any).acceptableSetCodes || [];
        const acceptableSets = existingWantEntry.acceptableSets || [];
        
        console.log('Edit mode - checking for cards to select:', {
          acceptedPrintingIds,
          acceptableSetCodes,
          acceptableSets,
          printingsDataLength: printingsData.length
        });
        
        // Check if this is an "Any Set" entry (empty acceptableSets means accept any version)
        const isAnySet = acceptableSets.length === 0 && acceptedPrintingIds.length === 0 && acceptableSetCodes.length === 0;
        
        if (isAnySet) {
          // "Any Set" mode - select ALL cards
          console.log('Any Set mode detected - selecting all cards');
          printingsData.forEach((card: any) => {
            matchingCardIds.add(card.id);
          });
        } else if (acceptedPrintingIds.length > 0) {
          // If we have specific printing IDs, only select those exact cards
          printingsData.forEach((card: any) => {
            if (acceptedPrintingIds.includes(card.id)) {
              console.log(`Selecting card by ID: ${card.id} - ${card.setName || card.set_name}`);
              matchingCardIds.add(card.id);
            }
          });
        } else {
          // Fallback to set-based matching if no specific IDs
          printingsData.forEach((card: any) => {
            const cardSetId = card.setId || card.set_id;
            const cardSetName = card.setName || card.set_name || card.set;
            
            if ((cardSetId && acceptableSetCodes.includes(cardSetId)) || 
                (cardSetName && acceptableSets.includes(cardSetName))) {
              matchingCardIds.add(card.id);
            }
          });
        }
        
        console.log('Selected card IDs:', Array.from(matchingCardIds));
        setSelectedCards(matchingCardIds);
        
        // Set quantity from existing entry
        setQuantity(existingWantEntry.quantity || 1);
        
        // Set foil preferences from existing entry
        const foilPrefs: string[] = [];
        if (existingWantEntry.acceptNonFoil) foilPrefs.push('nonfoil');
        if (existingWantEntry.acceptFoil) foilPrefs.push('foil');
        // A pre-Track-D entry has no acceptEtched; etched follows foil (the migration rule)
        if (existingWantEntry.acceptEtched ?? existingWantEntry.acceptFoil) foilPrefs.push('etched');
        // Always set foil preferences, even if empty (though that shouldn't happen)
        setFoilPreferences(foilPrefs.length > 0 ? foilPrefs : ['nonfoil']);
        
        // Set language preferences from existing entry
        if (existingWantEntry.acceptableLanguages && existingWantEntry.acceptableLanguages.length > 0) {
          setLanguagePreferences(existingWantEntry.acceptableLanguages);
        }
        
        // Set condition preferences from existing entry - use acceptableConditions if available
        if (existingWantEntry.acceptableConditions && existingWantEntry.acceptableConditions.length > 0) {
          // Convert backend format to frontend format if needed
          const conditionMap: { [key: string]: string } = {
            'NEAR_MINT': 'NM',
            'SLIGHTLY_PLAYED': 'LP',  // LP = Lightly Played
            'MODERATELY_PLAYED': 'MP',
            'HEAVILY_PLAYED': 'HP',
            'DAMAGED': 'DMG'
          };
          const conditions = existingWantEntry.acceptableConditions.map((c: string) => conditionMap[c] || c);
          setConditionPreferences(conditions);
        } else if (existingWantEntry.minCondition) {
          // Fallback for old minCondition field
          const conditionMap: { [key: string]: string[] } = {
            'NEAR_MINT': ['NM'],
            'SLIGHTLY_PLAYED': ['NM', 'LP'],
            'MODERATELY_PLAYED': ['NM', 'LP', 'MP'],
            'HEAVILY_PLAYED': ['NM', 'LP', 'MP', 'HP'],
            'DAMAGED': ['NM', 'LP', 'MP', 'HP', 'DMG']
          };
          const conditions = conditionMap[existingWantEntry.minCondition] || ['NM', 'LP', 'MP'];
          setConditionPreferences(conditions);
        }

        // Mark that we've initialized both preferences and cards from edit mode
        setHasInitializedPreferences(true);
        setHasInitializedCards(true);
      }
    } else {
      // Not in edit mode - select all cards by default for new entries
      if (!hasInitializedCards) {
        const allCardIds = new Set(printingsData.map((card: any) => card.id));
        setSelectedCards(allCardIds);
        setHasInitializedCards(true);
      }
    }
  }, [printingsData, isEditMode, existingWantEntry, hasInitializedCards, hasInitializedPreferences, isLoadingWantEntry]);
  
  // Calculate overall min and max prices from all cards
  const { overallMinPrice, overallMaxPrice } = useMemo(() => {
    if (!printingsData || printingsData.length === 0) {
      return { overallMinPrice: 0, overallMaxPrice: 0 };
    }
    
    let minPrice: number | null = null;
    let maxPrice: number | null = null;
    
    printingsData.forEach((card: any) => {
      let price: number | null = null;

      // Prefer pointValue (has language multiplier from backend)
      if (card.pointValue && card.pointValue > 0) {
        price = card.pointValue;
      } else {
        // Get price (prefer non-foil)
        if (card.price) {
          if (card.price.usd !== null && card.price.usd !== undefined) {
            price = decimalToPoints(card.price.usd);
          } else if (card.price.usdFoil !== null && card.price.usdFoil !== undefined) {
            price = decimalToPoints(card.price.usdFoil);
          }
        } else if (card.prices) {
          if (card.prices.usd !== null && card.prices.usd !== undefined) {
            price = decimalToPoints(card.prices.usd);
          } else if (card.prices.usdFoil !== null && card.prices.usdFoil !== undefined) {
            price = decimalToPoints(card.prices.usdFoil);
          }
        }

        // Apply language multiplier to raw prices
        if (price && languageMultiplier !== 1) {
          price = Math.round(price * languageMultiplier);
        }
      }

      if (price && price > 0) {
        if (minPrice === null || price < minPrice) minPrice = price;
        if (maxPrice === null || price > maxPrice) maxPrice = price;
      }
    });
    
    return {
      overallMinPrice: minPrice || 0,
      overallMaxPrice: maxPrice || 0
    };
  }, [printingsData, languageMultiplier]);

  // Finish chips no printing of this card exists in render disabled, never hidden (D-3).
  // An inert acceptEtched on such a card must not read as a choice (Edge-Case Ruling).
  /**
   * The finish RENDERED on a printing's image (Decision 12 as revised: gated on
   * the finish, never on the image source). Per printing: the first finish in
   * canonical order that the filter accepts AND the printing exists in; when
   * the filter accepts none of its finishes, the printing's own first finish.
   * An etched-only printing therefore always carries the sheen and the tag,
   * including at 140px in the artist groups; a foil+etched printing shows
   * etched once the filter narrows to etched.
   */
  const renderedFinishFor = useCallback(
    (printing: FinishSource): CardFinish => {
      const available = getAvailableFinishes(printing);
      const accepted = foilPreferences as CardFinish[];
      return FINISH_ORDER.find((f) => accepted.includes(f) && available.includes(f)) ?? available[0];
    },
    [foilPreferences],
  );

  const disabledFinishChips = useMemo(() => {
    if (!printingsData || printingsData.length === 0) return [];
    const present = new Set<CardFinish>();
    printingsData.forEach((p: any) => getAvailableFinishes(p).forEach((f) => present.add(f)));
    const labels: Record<CardFinish, string> = { nonfoil: 'Non-foil', foil: 'Foil', etched: 'Etched' };
    return (['nonfoil', 'foil', 'etched'] as CardFinish[]).filter((f) => !present.has(f)).map((f) => labels[f]);
  }, [printingsData]);

  // Compute unique sets with counts for the set filter dropdown
  const setOptions = useMemo(() => {
    if (!printingsData) return [];

    const setCounts = new Map<string, { code: string; name: string; count: number }>();

    printingsData.forEach((card: any) => {
      // Use setCode if available, fall back to setId (Scryfall set UUID)
      const setCode = card.setCode || card.set_code || card.setId || card.set_id;
      const setName = card.setName || card.set_name || setCode;

      if (setCode) {
        if (setCounts.has(setCode)) {
          setCounts.get(setCode)!.count++;
        } else {
          setCounts.set(setCode, { code: setCode, name: setName, count: 1 });
        }
      }
    });

    // Sort alphabetically by set name (A-Z)
    return Array.from(setCounts.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [printingsData]);

  // Group cards by selected criteria and sort by count
  const groupedCards = useMemo((): GroupedCards[] => {
    if (!printingsData) return [];

    // Filter by set if a set filter is active
    const filteredPrintings = setFilter
      ? printingsData.filter((card: any) => {
          const cardSetCode = card.setCode || card.set_code || card.setId || card.set_id;
          return cardSetCode === setFilter;
        })
      : printingsData;

    const groups: { [key: string]: CardPrinting[] } = {};

    // Group cards by selected criteria
    filteredPrintings.forEach((card: any) => {
      let groupKey: string;
      
      if (groupBy === 'Border') {
        groupKey = card.borderColor || card.border_color || 'unknown';
      } else {
        // Default to Artist grouping
        groupKey = card.artist || 'Unknown Artist';
      }
      
      
      // Get the image URL
      let imageUrl = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
      if (card.imageUris?.normal) {
        imageUrl = card.imageUris.normal;
      } else if (card.image_uris?.normal) {
        imageUrl = card.image_uris.normal;
      } else if (card.scryfallId) {
        // Use AssetUrls helper to construct CDN URL
        imageUrl = AssetUrls.cards.getCardImage(card.scryfallId, 'normal');
      }
      
      const cardPrinting: CardPrinting = {
        id: card.id,
        name: card.name,
        artist: card.artist || 'Unknown Artist',
        imageUrl: imageUrl,
        price: card.price || card.prices,  // Try both price and prices
        scryfallId: card.scryfallId,
        imageUris: card.imageUris,
        setName: card.setName || card.set_name,
        collectorNumber: card.collectorNumber || card.collector_number,
        foil: card.foil,
        nonFoil: card.nonFoil || card.nonfoil,
        finishes: card.finishes ?? null,
        promoTypes: card.promoTypes ?? card.promo_types ?? null,
        borderColor: card.borderColor || card.border_color,
        pointValue: card.pointValue,  // Add pointValue as fallback
        normal_fallbacks: card.normal_fallbacks  // Include fallback URLs from API
      } as any;
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(cardPrinting);
    });
    
    // Convert to array and calculate min/max prices
    const groupedArray: GroupedCards[] = Object.entries(groups).map(([groupKey, cards]) => {
      let minPrice: number | null = null;
      let maxPrice: number | null = null;
      
      cards.forEach(card => {
        const price = getCardPrice(card);

        if (price && price > 0) {
          if (minPrice === null || price < minPrice) minPrice = price;
          if (maxPrice === null || price > maxPrice) maxPrice = price;
        }
      });

      // Sort cards by price (descending - highest price first)
      cards.sort((a, b) => {
        const priceA: number = getCardPrice(a) || 0;
        const priceB: number = getCardPrice(b) || 0;

        // Sort in descending order (highest price first)
        return priceB - priceA;
      });
      
      // Format the label based on grouping type
      let groupLabel = groupKey;
      if (groupBy === 'Border') {
        // Format border color names
        const borderLabels: { [key: string]: string } = {
          'black': 'Black Border',
          'white': 'White Border',
          'silver': 'Silver Border',
          'gold': 'Gold Border',
          'borderless': 'Borderless',
          'unknown': 'Unknown Border'
        };
        groupLabel = borderLabels[groupKey] || (groupKey.charAt(0).toUpperCase() + groupKey.slice(1) + ' Border');
      }
      
      return {
        groupKey,
        groupLabel,
        cards,
        minPrice,
        maxPrice
      };
    });
    
    // Sort by number of cards (descending)
    groupedArray.sort((a, b) => b.cards.length - a.cards.length);
    
    return groupedArray;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printingsData, groupBy, setFilter, languageMultiplier]);
  
  const handleToggleCard = (cardId: number) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };
  
  /**
   * Hoisted above the selection helpers (it used to live further down) so the
   * memos below can use it. Price is the only filter applied here: `groupedCards`
   * has ALREADY applied the set filter, so re-checking the set would double-filter.
   */
  const isCardVisibleByPrice = useCallback((card: any): boolean => {
    const cardPrice = getCardPrice(card);

    // If card has no price, hide it when any price filter is applied
    if (cardPrice === null || cardPrice === 0) {
      return priceFilter.min === null && priceFilter.max === null;
    }

    if (priceFilter.min !== null && cardPrice < priceFilter.min) {
      return false;
    }

    if (priceFilter.max !== null && cardPrice > priceFilter.max) {
      return false;
    }

    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceFilter, languageMultiplier]);

  /**
   * The filter-visible printing ids per group, and the page totals derived from
   * them. Every "N" the user sees -- the count control, the menu labels, the
   * mobile sheet's apply button, the dock -- reads `visibleCount`, so they
   * cannot drift apart.
   */
  const visibleIdsByGroup = useMemo(
    () =>
      new Map<string, number[]>(
        groupedCards.map(g => [g.groupKey, g.cards.filter(isCardVisibleByPrice).map(c => c.id)])
      ),
    [groupedCards, isCardVisibleByPrice]
  );

  const allVisibleIds = useMemo(
    () => Array.from(visibleIdsByGroup.values()).flat(),
    [visibleIdsByGroup]
  );

  const allGroupKeys = useMemo(() => Array.from(visibleIdsByGroup.keys()), [visibleIdsByGroup]);

  /** N: filter-visible printings on the page. */
  const visibleCount = allVisibleIds.length;

  /** M: how many of those are selected. Matches what the submit path will send. */
  const selectedVisibleCount = useMemo(
    () => allVisibleIds.reduce((n, id) => (selectedCards.has(id) ? n + 1 : n), 0),
    [allVisibleIds, selectedCards]
  );

  /**
   * The ONE path every selection mutation goes through -- per-group Remove All
   * and Reset, the page-level menu, the mobile sheet, and list view's Select
   * All / Deselect All.
   *
   * Two things this fixes, both of which came from having three hand-rolled
   * paths instead of one:
   *
   * 1. `removedGroups` is what flips a rail between "Remove All" and "Reset".
   *    List view's Select All used to write `selectedCards` without touching
   *    it, so rails kept reading "Remove All" after everything was deselected.
   *    Passing the group keys alongside the ids keeps the two in lockstep.
   *
   * 2. Both setters MUST use the functional form. The old handlers snapshotted
   *    state from the render closure, so calling them in a loop (the obvious
   *    way to build a page-level clear) silently kept only the last
   *    iteration's writes -- React does not re-render between synchronous
   *    calls.
   *
   * `removedGroups` is presentation only; it never reaches the want-list
   * payload, which reads `selectedCards` alone.
   */
  const applySelection = useCallback(
    (ids: number[], groupKeys: string[], select: boolean) => {
      setSelectedCards(prev => {
        const next = new Set(prev);
        ids.forEach(id => (select ? next.add(id) : next.delete(id)));
        return next;
      });
      setRemovedGroups(prev => {
        const next = new Set(prev);
        groupKeys.forEach(k => (select ? next.delete(k) : next.add(k)));
        return next;
      });
    },
    []
  );

  const handleRemoveAll = (groupKey: string) => {
    applySelection(visibleIdsByGroup.get(groupKey) ?? [], [groupKey], false);
  };

  const handleReset = (groupKey: string) => {
    applySelection(visibleIdsByGroup.get(groupKey) ?? [], [groupKey], true);
  };

  /** Page-level equivalents. Same helper, every group at once. */
  const handleRemoveAllPrintings = () => {
    applySelection(allVisibleIds, allGroupKeys, false);
  };

  const handleSelectAllPrintings = () => {
    applySelection(allVisibleIds, allGroupKeys, true);
  };

  /**
   * Invert flips each filter-visible printing independently, so a group ends up
   * "removed" only if nothing in it survived the flip.
   */
  const handleInvertSelection = () => {
    const next = new Set(selectedCards);
    allVisibleIds.forEach(id => (next.has(id) ? next.delete(id) : next.add(id)));

    const removed = new Set<string>();
    visibleIdsByGroup.forEach((ids, key) => {
      if (ids.length > 0 && ids.every(id => !next.has(id))) removed.add(key);
    });

    setSelectedCards(next);
    setRemovedGroups(removed);
  };
  
  const handleAddToWantlist = async () => {
    // Check if user is logged in - show login modal if not
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    
    if (!printingsData || printingsData.length === 0) {
      showError('No card data available. Please try refreshing the page.');
      return;
    }
    
    // Handle edit mode
    if (isEditMode && editWantId) {
      const selectedCardData = printingsData.filter((card: any) =>
        selectedCards.has(card.id) && isCardVisible(card)
      );

      const setNames = selectedCardData.map((card: any) =>
        card.setName || card.set_name || card.set
      );
      const acceptableSets = Array.from(new Set(setNames)).filter(Boolean);

      if (acceptableSets.length === 0) {
        showError('Please select at least one printing.');
        return;
      }

      // Get the specific card IDs that are selected (same as in add mode)
      const acceptedPrintingIds = selectedCardData.map((card: any) => card.id);
      
      try {
        // Convert condition preferences to the acceptableConditions format
        const conditionMapping: { [key: string]: string } = {
          'NM': 'NEAR_MINT',
          'LP': 'SLIGHTLY_PLAYED',  // LP = Lightly Played
          'MP': 'MODERATELY_PLAYED',
          'HP': 'HEAVILY_PLAYED',
          'DMG': 'DAMAGED'
        };
        
        const acceptableConditions = conditionPreferences.map(c => conditionMapping[c] || c);
        
        console.log('=== UPDATING WANT LIST ===');
        console.log('ID:', editWantId);
        console.log('Quantity being sent:', quantity);
        console.log('Current quantity:', existingWantEntry?.quantity);
        console.log('AcceptedPrintingIds:', acceptedPrintingIds);
        
        // First, update the preferences (without quantity)
        const updateResult = await updateWantListPreferences({
          id: Number(editWantId),
          acceptableSets,
          acceptFoil: foilPreferences.includes('foil'),
          acceptNonFoil: foilPreferences.includes('nonfoil'),
          acceptEtched: foilPreferences.includes('etched'),
          acceptableLanguages: mapFrontendToBackend(languagePreferences),
          acceptableConditions,
          acceptedPrintingIds, // Include specific card IDs to preserve exact selections
        }).unwrap();
        
        console.log('Preferences update result:', updateResult);
        if (updateResult?.adjustment) showFinishAdjustment(updateResult.adjustment);
        
        // Then, if quantity has changed, update it using the quantity-specific endpoint
        if (existingWantEntry && quantity !== existingWantEntry.quantity) {
          const quantityChange = quantity - existingWantEntry.quantity;
          console.log('Updating quantity with change:', quantityChange);
          
          await updateQuantity({
            id: Number(editWantId),
            change: quantityChange,
          }).unwrap();
          
          console.log('Quantity updated successfully');
        }
        
        showCardAddedSuccess(decodedCardName, quantity);
        
        // Navigate back to want list
        navigate('/my-cards/want-list');
      } catch (error) {
        console.error('Failed to update want list:', error);
        showError('Failed to update want list. Please try again.');
      }
      return;
    }
    
    // Get oracle ID - check all possible locations
    const firstPrinting = printingsData[0];
    const oracleId =
      (cardDetails as any)?.oracleId ||  // Check root level first (this is where it actually is)
      searchOracleId ||
      firstPrinting.oracleId ||
      (firstPrinting as any).oracle_id ||
      (firstPrinting as any).oracleID;  // Check different casings

    // The card name should come from the URL parameter, not the printing data
    const cardName = decodedCardName || firstPrinting.name;

    if (!oracleId) {
      console.error('No oracle ID found. Card details:', cardDetails, 'Search oracleId:', searchOracleId, 'Printing data:', firstPrinting);
      showError('Unable to add card - missing card identifier. Please contact support if this persists.');
      return;
    }
    
    // Get the selected cards and their sets
    const selectedCardData = printingsData.filter((card: any) =>
      selectedCards.has(card.id) && isCardVisible(card)
    );
    
    // Group selected cards by border color
    const cardsByBorder = selectedCardData.reduce((acc: any, card: any) => {
      const borderColor = card.borderColor || card.border_color || 'black';
      if (!acc[borderColor]) {
        acc[borderColor] = [];
      }
      acc[borderColor].push(card);
      return acc;
    }, {});
    
    // Log border grouping for debugging
    console.log('Selected cards grouped by border:', {
      borders: Object.keys(cardsByBorder),
      counts: Object.entries(cardsByBorder).map(([border, cards]: [string, any]) => 
        `${border}: ${cards.length} cards`
      )
    });
    
    // Log border types for debugging
    const borderTypes = Object.keys(cardsByBorder);
    if (borderTypes.length > 1) {
      console.log(`✓ Multiple border types selected: ${borderTypes.join(', ')}`);
      console.log('Backend now supports multiple want records for different criteria.');
    }
    
    // Extract unique set names from selected cards
    const setNames = selectedCardData.map((card: any) => 
      card.setName || card.set_name || card.set
    );
    const acceptableSets = Array.from(new Set(setNames)).filter(Boolean);
    
    if (acceptableSets.length === 0) {
      showError('Please select at least one printing to add to your want list.');
      return;
    }
    
    // Get the specific card IDs that are selected
    const acceptedPrintingIds = selectedCardData.map((card: any) => card.id);
    
    // Convert condition preferences to backend format
    const conditionMapping: { [key: string]: string } = {
      'NM': 'NEAR_MINT',
      'LP': 'SLIGHTLY_PLAYED',
      'MP': 'MODERATELY_PLAYED',
      'HP': 'HEAVILY_PLAYED',
      'DMG': 'DAMAGED'
    };
    
    const acceptableConditions = conditionPreferences.map(c => conditionMapping[c] || c);
    
    const requestPayload: AddFromPrintingsRequest = {
      cardName: cardName,
      oracleId: oracleId,
      quantity: quantity,
      acceptableSets: acceptableSets,
      acceptableLanguages: mapFrontendToBackend(languagePreferences), // Convert to backend codes
      acceptFoil: foilPreferences.includes('foil'), // Use actual foil preferences
      acceptNonFoil: foilPreferences.includes('nonfoil'), // Use actual non-foil preferences
      acceptEtched: foilPreferences.includes('etched'),
      acceptableConditions: acceptableConditions, // Use new granular conditions field
      acceptedPrintingIds: acceptedPrintingIds, // Pass specific card IDs to prevent mismatch
      source: navFrom === 'home' ? 'home-feed' : fromSearch ? 'search' : 'card-details', // telemetry: which surface opened the picker
      // TODO: Add borderColors field when backend supports it
      // borderColors: borderTypes,
    };
    
    
    // Quick health check before making request
    const healthCheck = await checkEndpointHealth('/want-list/add-from-printings', true);
    if (!healthCheck.isHealthy && healthCheck.error?.includes('Connection failed')) {
      console.warn('Backend may be unavailable:', healthCheck.error);
    }
    
    try {
      const result = await addFromPrintings(requestPayload).unwrap();
      if (result?.adjustment) showFinishAdjustment(result.adjustment);

      console.log('Successfully added to want list:', result);
      console.log('Response details:', {
        id: result?.id,
        isNew: result?.id ? 'Check if this is a new ID' : 'No ID returned',
        acceptableSets: result?.acceptableSets,
        quantity: result?.quantity
      });
      
      // Show success with details about what was added
      const borderInfo = Object.keys(cardsByBorder).join(', ');
      
      // The backend now properly creates separate want records
      showCardAddedSuccess(cardName, quantity);

      // Navigate back to search if user came from search results
      if (fromSearch && returnPath) {
        navigate(returnPath);
      }

      // Log details for debugging
      console.log('Want record created successfully:', {
        id: result?.id,
        cardName: cardName,
        sets: acceptableSets,
        borders: borderInfo
      });
    } catch (error: any) {
      console.error('Failed to add to want list:', error);
      console.error('Full error data:', error?.data);
      console.error('Error details:', {
        status: error?.status,
        data: error?.data,
        errors: error?.data?.errors,
        message: error?.data?.message
      });
      
      // Build error message from validation errors if available
      if (error?.status === 500) {
        // Check if this might be a user ID issue
        const userId = getCurrentUserId();
        if (!userId) {
          showAuthError('Session expired or invalid. Please log in again.');
          console.error('No user ID found in token - user may need to log in');
        } else if (userId > 5) {
          showAuthError('Authentication issue detected. Please log out and log back in to refresh your session.');
          console.error('Possible invalid user ID in token:', userId);
        } else {
          showError('Server error occurred. Please check backend logs or contact support.');
          console.error('Backend returned 500 for valid user ID:', userId);
        }
        console.error('Backend server error - check backend logs for details');
        // Log the full request for debugging
        console.error('Failed request payload was:', requestPayload);
        console.error('Current user ID from token:', userId);
        
        // Save failed request to localStorage for recovery
        try {
          const failedRequests = JSON.parse(localStorage.getItem('failedWantListRequests') || '[]');
          failedRequests.push({
            timestamp: new Date().toISOString(),
            payload: requestPayload,
            error: 'Server Error 500'
          });
          // Keep only last 10 failed requests
          if (failedRequests.length > 10) {
            failedRequests.shift();
          }
          localStorage.setItem('failedWantListRequests', JSON.stringify(failedRequests));
          console.log('Failed request saved to localStorage for recovery');
        } catch (e) {
          console.error('Could not save failed request to localStorage:', e);
        }
      } else if (error?.data?.errors && Array.isArray(error.data.errors)) {
        const errorMessages = error.data.errors.map((e: any) => e.msg || e.message);
        showValidationError(errorMessages);
      } else if (error?.data?.message) {
        showError(error.data.message);
      } else {
        showError(`Failed to add ${cardName} to want list. Please try again.`);
      }
    }
  };
  
  // Check if a card should be visible based on set filter
  const isCardVisibleBySet = (card: any): boolean => {
    if (setFilter === null) return true; // "All Sets" = show everything
    const cardSetCode = card.setCode || card.set_code;
    return cardSetCode === setFilter;
  };

  // Combined visibility check for price and set filters
  const isCardVisible = (card: any): boolean => {
    return isCardVisibleByPrice(card) && isCardVisibleBySet(card);
  };

  const handlePriceChange = (min: number, max: number) => {
    setPriceFilter({ min, max });
  };

  if (isLoading) {
    return (
      <div className="view-card-printings-container">
        {/* Mobile Menu Overlay - click to close */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <LeftNavigationBar
          currentTab={currentTab}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
          isPublicMode={!currentUser}
          onNavigationAttempt={() => setShowLoginModal(true)}
          isAuthenticated={!!currentUser}
        />
        <div className="view-card-printings-content">
          <div className="view-card-printings-bg" />
          <div className="view-card-printings-topnav">
            <TopNavigationBar
              title="View Card Printings"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
          </div>
          <div className="view-card-printings-main">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4rem',
              gap: '1rem'
            }}>
              <LoadingIndicator />
              <div style={{ color: 'white', fontSize: '16px', marginTop: '1rem' }}>
                Loading printings for {decodedCardName}...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || (!printingsData && !isLoading)) {
    return (
      <div className="view-card-printings-container">
        {/* Mobile Menu Overlay - click to close */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <LeftNavigationBar
          currentTab={currentTab}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
          isPublicMode={!currentUser}
          onNavigationAttempt={() => setShowLoginModal(true)}
          isAuthenticated={!!currentUser}
        />
        <div className="view-card-printings-content">
          <div className="view-card-printings-bg" />
          <div className="view-card-printings-topnav">
            <TopNavigationBar
              title="View Card Printings"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
          </div>
          <div className="view-card-printings-main">
            <div style={{ padding: '2rem', textAlign: 'center', color: 'white' }}>
              No printings found for {decodedCardName}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get background image from first card
  const firstCard = printingsData?.[0] as any;
  const backgroundImage = firstCard?.imageUris?.art_crop;

  return (
    <div className="view-card-printings-container">
      {/* Mobile Menu Overlay - click to close */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <LeftNavigationBar
        currentTab={currentTab}
        isMobile={isMobile}
        isMobileMenuOpen={isMobileMenuOpen}
        onMenuClose={() => setIsMobileMenuOpen(false)}
        isPublicMode={!currentUser}
        onNavigationAttempt={() => setShowLoginModal(true)}
        isAuthenticated={!!currentUser}
      />

      {/* Content Area */}
      <div className="view-card-printings-content">
        {/* Background with Blur */}
        <div
          className="view-card-printings-bg"
          style={backgroundImage ? {
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(110px)'
          } : undefined}
        />

        {/* Top Navigation */}
        <div className="view-card-printings-topnav">
          <TopNavigationBar
            title={`${decodedCardName} - All Printings`}
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />
        </div>

        {/* Main Body */}
        <div className="view-card-printings-main">
          <CardPrintingsHeader
            selectedCount={selectedVisibleCount}
            onAddToWantlist={handleAddToWantlist}
            onPriceChange={handlePriceChange}
            buttonText={isEditMode ? 'Update Want List' : 'Add to Want List'}
            headerTitle={isEditMode ? 'Update desired versions' : undefined}
            headerSubtitle={isEditMode ? "Select versions of this card that you'd like to receive to update the want list record for this card." : undefined}
            defaultQuantity={quantity}
            initialFoil={foilPreferences.includes('foil')}
            initialRegular={foilPreferences.includes('nonfoil')}
            initialEtched={foilPreferences.includes('etched')}
            disabledFinishes={disabledFinishChips}
            initialLanguages={languagePreferences}
            initialConditions={conditionPreferences}
            isAddingToWantlist={isEditMode ? isUpdatingWantList : isAddingToWantList}
            onGroupChange={(val) => {
              setGroupBy(val);
              setRemovedGroups(new Set()); // Clear removed groups when changing groupBy
            }}
            onQtyChange={(val) => {
              console.log('Quantity changed to:', val);
              setQuantity(val);
            }}
            onFiltersChange={(filters: any) => {
              // The header emits the COMPLETE filter state every time, so an
              // empty array is a real value ("the user cleared this dimension")
              // rather than "this message isn't about that dimension". The old
              // `length > 0` guards made clearing impossible and silently kept
              // shipping the stale value in the want-list payload.
              const foilMap: { [key: string]: string } = {
                'Foil': 'foil',
                'Non-foil': 'nonfoil',
                'Etched': 'etched',
              };
              setFoilPreferences((filters.foil ?? []).map((f: any) => foilMap[f] || f.toLowerCase()));
              setLanguagePreferences(filters.languages ?? []);
              setConditionPreferences(filters.conditions ?? []);
            }}
            minPrice={overallMinPrice}
            maxPrice={overallMaxPrice}
            sets={setOptions}
            selectedSet={setFilter}
            onSetChange={(setCode) => setSetFilter(setCode)}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            totalCount={visibleCount}
            groupBy={groupBy}
            onSelectAll={handleSelectAllPrintings}
            onRemoveAll={handleRemoveAllPrintings}
            onInvertSelection={handleInvertSelection}
          />

          {/* List View */}
          {viewMode === 'list' ? (
            <div className="printings-list-view">
              {/* Select All / Deselect All */}
              <div className="list-view-controls">
                <button className="list-view-select-btn" onClick={handleSelectAllPrintings}>
                  Select All
                </button>
                <button className="list-view-select-btn" onClick={handleRemoveAllPrintings}>
                  Deselect All
                </button>
              </div>

              {groupedCards.map((group) => {
                const visibleCards = group.cards.filter(c => isCardVisibleByPrice(c));
                const groupCardIds = visibleCards.map(c => c.id);
                const allGroupSelected = groupCardIds.length > 0 && groupCardIds.every(id => selectedCards.has(id));
                const someGroupSelected = groupCardIds.some(id => selectedCards.has(id));

                return (
                  <div key={group.groupKey} className="list-view-group">
                    {/* Group Header */}
                    <div className="list-view-group-header">
                      <label className="list-view-group-checkbox-label">
                        <input
                          type="checkbox"
                          checked={allGroupSelected}
                          ref={(el) => { if (el) el.indeterminate = someGroupSelected && !allGroupSelected; }}
                          onChange={() => {
                            setSelectedCards(prev => {
                              const next = new Set(prev);
                              if (allGroupSelected) {
                                groupCardIds.forEach(id => next.delete(id));
                              } else {
                                groupCardIds.forEach(id => next.add(id));
                              }
                              return next;
                            });
                          }}
                          className="list-view-checkbox"
                        />
                        <span className="list-view-group-name">{group.groupLabel}</span>
                      </label>
                      <span className="list-view-group-count">
                        {visibleCards.length} version{visibleCards.length !== 1 ? 's' : ''}
                      </span>
                      <div className="list-view-group-prices">
                        {group.minPrice != null && <><span className="list-view-price-label">Low</span> <Points points={group.minPrice} type="small" /></>}
                        {group.maxPrice != null && <><span className="list-view-price-label">High</span> <Points points={group.maxPrice} type="small" /></>}
                      </div>
                    </div>

                    {/* Table */}
                    <table className="list-view-table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th style={{ width: 50 }}></th>
                          <th>Set</th>
                          <th style={{ width: 60 }}>#</th>
                          <th style={{ width: 220 }}>Finish</th>
                          <th style={{ width: 80 }}>Condition</th>
                          <th style={{ width: 80 }}>Language</th>
                          <th style={{ width: 80, textAlign: 'right' }}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleCards.map((card) => {
                          const isSelected = selectedCards.has(card.id);
                          const cardPrice = getCardPrice(card);
                          return (
                            <tr
                              key={card.id}
                              className={`list-view-row ${isSelected ? 'selected' : 'deselected'}`}
                              onClick={() => handleToggleCard(card.id)}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleCard(card.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="list-view-checkbox"
                                />
                              </td>
                              <td>
                                <div
                                  className="list-view-thumbnail-wrapper"
                                  onMouseEnter={(e) => card.scryfallId && handleCardHover(e, card.scryfallId, card.name)}
                                  onMouseLeave={handleCardHoverLeave}
                                >
                                  <FoilCardImage
                                    src={card.imageUrl}
                                    alt={card.name}
                                    finish={renderedFinishFor(card)}
                                    promoTypes={card.promoTypes}
                                    borderRadius="4px"
                                    style={{ height: 44, width: 'auto' }}
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).src = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                                    }}
                                  />
                                </div>
                              </td>
                              <td className="list-view-cell-set">{card.setName}</td>
                              <td>{card.collectorNumber}</td>
                              {/* D-4: one combined Finish column, finish marks plus promo chips */}
                              <td className="list-view-cell-finish">
                                <FinishMarks
                                  finishes={getAvailableFinishes(card)}
                                  promoTypes={card.promoTypes}
                                  size="sm"
                                />
                              </td>
                              <td>{conditionPreferences.join(', ')}</td>
                              <td>{languagePreferences.join(', ')}</td>
                              <td style={{ textAlign: 'right' }}>
                                {cardPrice !== null && cardPrice > 0 ? (
                                  <Points points={cardPrice} type="small" />
                                ) : (
                                  <span style={{ color: 'var(--gold)', fontFamily: 'Georgia', fontSize: '11px' }}>???</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}

              {/* Card image tooltip */}
              {hoveredCard && (
                <CardImageTooltip
                  scryfallId={hoveredCard.scryfallId}
                  cardName={hoveredCard.cardName}
                  position={tooltipPosition}
                  onMouseEnter={() => {}}
                  onMouseLeave={handleCardHoverLeave}
                />
              )}
            </div>
          ) : (
            /* Image View (existing) */
            <CardPrintingsContainer>
              {groupedCards.map((group) => (
                <GenericGroupContainer
                  key={group.groupKey}
                  title={`${group.cards.length} version${group.cards.length !== 1 ? 's' : ''} available`}
                  lowPrice={group.minPrice || 0}
                  highPrice={group.maxPrice || 0}
                  groupName={group.groupLabel}
                  onRemoveAll={() => handleRemoveAll(group.groupKey)}
                  onReset={() => handleReset(group.groupKey)}
                  isRemoved={removedGroups.has(group.groupKey)}
                >
                  <div className="card-grid">
                    {group.cards.map((card) => {
                      const renderedFinish = renderedFinishFor(card);

                      // Calculate price for display (with language multiplier)
                      const cardPrice = getCardPrice(card);

                      // Check if card is visible based on price filter
                      // Note: Set filtering is already done in groupedCards useMemo, so we only check price here
                      const isVisibleByPrice = isCardVisibleByPrice(card);

                      // Card is "selected" (visible) only if it's both selected AND passes price filter
                      const isCardSelected = selectedCards.has(card.id) && isVisibleByPrice;

                      return (
                        <div
                          key={card.id}
                          className={`card-item ${isCardSelected ? 'selected' : ''}`}
                          onClick={() => handleToggleCard(card.id)}
                          style={{ cursor: 'pointer', position: 'relative' }}
                        >
                          {!isCardSelected && (
                            <div className="deselected-overlay">
                              <img
                                className="heart-icon broken"
                                src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                                alt="Not wanted"
                              />
                              <img
                                className="heart-icon unbroken"
                                src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/heart.svg"
                                alt="Add to want"
                              />
                            </div>
                          )}
                          <FoilCardImage
                            className="card-item-image"
                            src={card.imageUrl}
                            alt={`${card.name} - ${card.setName}`}
                            finish={renderedFinish}
                            promoTypes={card.promoTypes}
                            borderRadius="8px"
                            onError={(e) => {
                              const img = e.currentTarget;
                              const currentSrc = img.src;

                              // First try backend-provided fallback URLs if available
                              const fallbacks = (card as any).normal_fallbacks;
                              if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                                img.dataset.fallbackAttempted = 'true';
                                img.src = fallbacks[0];
                                return;
                              }

                              // Otherwise, try different image formats using scryfallId
                              if (card.scryfallId && !img.dataset.formatFallbackComplete) {
                                // Determine current format from URL
                                if (currentSrc.includes('/normal/')) {
                                  // Normal format failed, try small
                                  img.src = AssetUrls.cards.getCardImage(card.scryfallId, 'small');
                                  return;
                                } else if (currentSrc.includes('/small/')) {
                                  // Small format failed, try large
                                  img.src = AssetUrls.cards.getCardImage(card.scryfallId, 'large');
                                  return;
                                } else if (currentSrc.includes('/large/')) {
                                  // Large format failed, mark as complete and use placeholder
                                  img.dataset.formatFallbackComplete = 'true';
                                }
                              }

                              // If all fallbacks failed, use placeholder
                              img.src = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                            }}
                          />
                          <div className="card-item-points">
                            {cardPrice !== null && cardPrice > 0 ? (
                              <Points points={cardPrice} type="small" />
                            ) : (
                              <div style={{
                                position: 'relative',
                                padding: '2px',
                                borderRadius: '24px',
                                background: 'linear-gradient(90deg, #f6e3bf, #634f21)',
                                height: '18px',
                                width: '65px'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  position: 'absolute',
                                  top: '1px',
                                  bottom: '1px',
                                  left: '1px',
                                  right: '1px',
                                  padding: '1.5px',
                                  borderRadius: '24px',
                                  backgroundColor: 'rgba(0, 0, 0, 0.85)'
                                }}>
                                  <img
                                    src={AssetUrls.icons.profile.coin}
                                    width={16}
                                    height={16}
                                    alt="coin-image"
                                    style={{ paddingLeft: '1px' }}
                                  />
                                  <span style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    fontSize: '11px',
                                    color: 'var(--gold)',
                                    fontFamily: 'Georgia',
                                    marginLeft: '2px',
                                    marginRight: '2px',
                                    paddingBottom: '2px'
                                  }}>???</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </GenericGroupContainer>
              ))}
            </CardPrintingsContainer>
          )}
        </div>

        {/* Footer */}
        <div className="view-card-printings-footer">
          <Footer />
        </div>

        {/*
          Mobile commit dock. A SIBLING of the scrolling content, not part of the
          header -- the commit button used to scroll out of reach on a phone.
          The content column carries matching bottom padding so the last group
          is fully scrollable above it rather than hidden underneath.
        */}
        <div className="cp-mobile-dock">
          <div className="cp-mobile-dock-qty">
            <QtyControl defaultValue={quantity} onChange={(v) => setQuantity(v)} />
          </div>
          <ActionButton
            onPress={handleAddToWantlist}
            isLoading={isEditMode ? isUpdatingWantList : isAddingToWantList}
            isDisabled={selectedVisibleCount === 0}
          >
            <span>
              {isEditMode ? 'Update' : 'Add'} {selectedVisibleCount} to Want List
            </span>
          </ActionButton>
        </div>
      </div>

      {/* Login prompt modal for unauthenticated users */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername={decodedCardName}
      />
    </div>
  );
}
